import { isDefined } from "@clipboard-health/util-ts";
import { InputAdornment, Stack } from "@mui/material";
import { TERMS_OF_SERVICE_URL } from "@src/appV2/ContactCBHInfo/constants";
import { ExternalLink } from "@src/appV2/lib/Links/ExternalLink";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { AgreementLink } from "@src/appV2/WorkplaceAgreement/AgreementLink";
import {
  AGREE_AND_ACCEPT_LABEL,
  WorkplaceAgreementDialogShell,
} from "@src/appV2/WorkplaceAgreement/WorkplaceAgreementDialogShell";
import { isEmpty } from "lodash";
import { useEffect, useRef } from "react";

export interface WorkplaceAgreementDialogViewProps {
  isOpen: boolean;
  legalEntityName: string;
  onLegalEntityNameChange: (legalEntityName: string) => void;
  /** A backfilled value is shown read-only behind an "Edit" affordance. */
  hasPrefilledLegalEntityName: boolean;
  /** True once the user has deliberately opted into editing a backfilled value. */
  isEditingLegalEntityName: boolean;
  onEditLegalEntityName: () => void;
  legalEntityNameError?: string;
  flexAgreementUrl: string;
  hiringAgreementUrl?: string;
  isSubmitting: boolean;
  onAgree: () => void;
}

/**
 * "on" variation: gates the whole workplace once, and captures the legal entity name that signs
 * the Marketplace Access Agreements.
 */
export function WorkplaceAgreementDialogView(props: WorkplaceAgreementDialogViewProps) {
  const {
    isOpen,
    legalEntityName,
    onLegalEntityNameChange,
    hasPrefilledLegalEntityName,
    isEditingLegalEntityName,
    onEditLegalEntityName,
    legalEntityNameError,
    flexAgreementUrl,
    hiringAgreementUrl,
    isSubmitting,
    onAgree,
  } = props;

  // With nothing to confirm, the field is editable from the start; a backfilled value stays
  // read-only until the user deliberately unlocks it.
  const isLegalEntityNameEditable = isEditingLegalEntityName || !hasPrefilledLegalEntityName;

  // Focus the field only once the user unlocks it via "Edit" — not on mount (avoids the static
  // `autoFocus` attribute, which jsx-a11y flags for unconditionally stealing focus).
  const legalEntityNameInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditingLegalEntityName) {
      legalEntityNameInputRef.current?.focus();
    }
  }, [isEditingLegalEntityName]);

  return (
    <WorkplaceAgreementDialogShell
      isOpen={isOpen}
      title="Marketplace Access Agreements"
      isSubmitting={isSubmitting}
      isAgreeDisabled={isEmpty(legalEntityName.trim())}
      onAgree={onAgree}
    >
      <Stack spacing={5}>
        <TextInput
          fullWidth
          inputRef={legalEntityNameInputRef}
          label="Legal entity name"
          value={legalEntityName}
          error={Boolean(legalEntityNameError)}
          helperText={legalEntityNameError}
          InputProps={{
            readOnly: !isLegalEntityNameEditable,
            endAdornment:
              hasPrefilledLegalEntityName && !isEditingLegalEntityName ? (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    variant="link"
                    startIconType="pencil"
                    sx={(theme) => ({ flexShrink: 0, gap: theme.spacing(2) })}
                    onClick={onEditLegalEntityName}
                  >
                    Edit
                  </Button>
                </InputAdornment>
              ) : undefined,
          }}
          onChange={(event) => {
            onLegalEntityNameChange(event.target.value);
          }}
        />

        <Text variant="body2" sx={{ color: "base.muted" }}>
          Please carefully read our{" "}
          <ExternalLink to={TERMS_OF_SERVICE_URL}>Terms of Service</ExternalLink> and Marketplace
          Access Agreements. By clicking &apos;{AGREE_AND_ACCEPT_LABEL}&apos;, you represent and
          warrant that you have the authority to bind the organization associated with this account,
          that you reviewed and confirmed the legal entity name above as accurate, and that you have
          read, understood, and agree to be bound by the Terms of Service and Marketplace Access
          Agreements listed below.
        </Text>

        <Stack spacing={3}>
          <AgreementLink to={flexAgreementUrl}>Flex Marketplace Access Agreement</AgreementLink>
          {isDefined(hiringAgreementUrl) && !isEmpty(hiringAgreementUrl) && (
            <AgreementLink to={hiringAgreementUrl}>
              Hiring Marketplace Access Agreement
            </AgreementLink>
          )}
        </Stack>
      </Stack>
    </WorkplaceAgreementDialogShell>
  );
}
